import { mul, primaryMatrix, targets } from '../test-runtime/northstarMaths';
import { ASSET_KEYS, verifyPackage } from './profile';
import { sha256 } from './identity';

const PRIMARIES = [[.64, .33, 58], [.30, .60, 185], [.15, .06, 22]];
const asXyY = xyz => { const total = xyz.reduce((sum, value) => sum + value, 0); return total > 0 ? [xyz[0] / total, xyz[1] / total, xyz[1]] : [.3127, .329, 0]; };
const asciiBytes = value => Uint8Array.from(value, character => character.charCodeAt(0));
export function createSimulationBench() {
  let rgb = [255, 255, 255], brightness = 100, channels = null, scale = 1;
  const matrix = primaryMatrix(PRIMARIES);
  return {
    mode: 'simulation',
    async prepare() { return ['meter', 'generator', 'monitor'].map(key => ({ key, identity: `SIMULATED ${key}`, settings: 'Synthetic deterministic panel', calibrationDue: null })); },
    async setRgb(r, g, b) { rgb = [r, g, b]; },
    async setBacklight(value) { brightness = value; },
    async measure() {
      const codes = channels ? [...'RGB'].map((channel, index) => channels[channel][rgb[index]]) : rgb;
      return asXyY(mul(matrix, PRIMARIES.map((primary, index) => primary[2] * (codes[index] / 255) ** 2.2 * brightness / 100 * scale)));
    },
    async loaded(calibration) { channels = calibration.channels; scale = calibration.backlightPercent / 100; },
    async close() {},
  };
}

export async function createDemoPackage() {
  const target = [.3127, .329, 180], coefficients = targets(PRIMARIES, target), matrix = primaryMatrix(PRIMARIES), ecdis = [];
  for (const [mode, brightness] of [['Day', 100], ['Dusk', 45], ['Night', 6]]) {
    const referenceLuminance = target[2] * (200 / 255) ** 2.2 * brightness / 100;
    for (const [name, rgb] of [['SYNTHETIC_WHITE', [200, 200, 200]], ['SYNTHETIC_RED', [180, 40, 40]], ['SYNTHETIC_GREY', [100, 100, 100]]]) {
      const xyz = mul(matrix, coefficients.map((value, index) => value * (rgb[index] / 255) ** 2.2 * brightness / 100));
      ecdis.push({ name, mode, brightness, rgb, xyY: asXyY(xyz), referenceLuminance, deltaLimit: 16, luminanceWindow: [.8, 1.2] });
    }
  }
  const profile = { schemaVersion: 'northstar-profile/1', id: 'synthetic-demo', revision: '1', monitorType: 'TEST NORTHSTR', status: 'experimental',
    target: { x: target[0], y: target[1], luminance: target[2] }, gamma: 2.2, timingNumber: 561,
    station: { id: 'SIMULATED', settleSeconds: .01, backlightSettleSeconds: .01, repeatabilityRelative: .005, meterSettings: 'Synthetic deterministic panel', calibrationDue: '2099-12-31' },
    limits: { whiteRelative: .05, whiteDeltaUv: .005, additiveRelative: .03, minimumContrast: 100, driftRelative: .01, signalRange: [.5, 2],
      primaryBounds: PRIMARIES.map(primary => ({ x: [primary[0] - .02, primary[0] + .02], y: [primary[1] - .02, primary[1] + .02] })),
      gammaBands: [{ from: 16, to: 31, relative: .1 }, { from: 32, to: 63, relative: .06 }, { from: 64, to: 255, relative: .03 }] },
    ecdis, assets: {}, qualification: { signalMapping: false, meterSettings: false, assets: false, ecdisTable: false } };
  const edid = new Uint8Array(128); edid.set([0, 255, 255, 255, 255, 255, 255, 0]); edid.set([1, 3], 18);
  edid.set([0, 0, 0, 252, 0], 90); edid.set(asciiBytes('TEST DISPLAY\n'), 95); edid[127] = (256 - edid.slice(0, 127).reduce((sum, value) => sum + value, 0) % 256) % 256;
  const assets = { baseEdid: edid,
    baseDimming: asciiBytes('#Dimming table\r\n#line|Duty-cycle|ON-time|DAC|LED|OSD\r\n' + Array.from({ length: 101 }, (_, index) => `${index};8000;${Math.max(1, index * 80)};0;255;${index};\r\n`).join('')),
    baseTiming: asciiBytes('1920;1080;88;44;148;4;5;5;36;\r\n'), baseCfg: asciiBytes('ETHERNET=NO\r\nDEBUG_CONSOLE=NO\r\nSAAB_CONFIG=NO\r\nSAAB_POT_SCALE=NO\r\n') };
  for (const key of ASSET_KEYS) profile.assets[key] = { name: `synthetic-${key}`, sha256: await sha256(assets[key]) };
  return verifyPackage(profile, assets);
}
